import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { getDataAPIAuth, postDataAPI } from "../../utils/API";
import GlobalTypes from "../../redux/actions/GlobalTypes";
import { dialog } from "../Common/TreasuryDialog";
import axios from "axios";
import moment from 'moment';
import { SARAmount, formatSAR } from "../../utils/currency";

// Mobile breakpoint hook
const useIsMobile = (bp = 900) => {
  const [isMobile, setIsMobile] = React.useState(() => window.innerWidth <= bp);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= bp);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [bp]);
  return isMobile;
};

const API = process.env.REACT_APP_API_URL || "";

// ─── Design tokens ────────────────────────────────────────────────────────────
const DARK   = '#1a3c2e';
const GOLD   = '#c9a84c';
const GREEN  = '#16a34a';
const RED    = '#dc2626';
const BG     = '#f4f6f4';
const CARD   = '#ffffff';
const BORDER = '#e5e7eb';
const MUTED  = '#6b7280';
const MUTED2 = '#9ca3af';

// ─── Helpers ──────────────────────────────────────────────────────────────────
// fmt: formats number with Riyal symbol on LEFT + comma thousands
const fmt = (n) => `﷼ ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const StatPill = ({ label, value, valueColor }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: MUTED2, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
        <span style={{ fontSize: 15, fontWeight: 700, color: valueColor || '#111827' }}>{value}</span>
    </div>
);

const SectionCard = ({ children, style }) => (
    <div style={{ background: CARD, borderRadius: 14, border: `1px solid ${BORDER}`, boxShadow: '0 1px 8px rgba(0,0,0,0.05)', overflow: 'hidden', ...style }}>
        {children}
    </div>
);

const CardHeader = ({ title, subtitle, isAr }) => (
    <div style={{ padding: '14px 20px', borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexDirection: isAr ? 'row-reverse' : 'row' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: DARK, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{title}</span>
        {subtitle && <span style={{ fontSize: 12, color: MUTED2 }}>{subtitle}</span>}
    </div>
);

// ─── Custom SVG Price Chart ───────────────────────────────────────────────────
const RANGES = [
    { label: '1D',  days: 1 },
    { label: '1W',  days: 7 },
    { label: '1M',  days: 30 },
    { label: '3M',  days: 90 },
    { label: '6M',  days: 180 },
    { label: 'All', days: null },
];

const PriceChart = ({ chartdata }) => {
    const [range, setRange]       = useState('1W');
    const [hoverIdx, setHoverIdx] = useState(null);
    const svgRef                  = useRef(null);

    if (!chartdata || chartdata.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '48px 24px', color: MUTED2, fontSize: 13 }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>📈</div>
                No price data available yet
            </div>
        );
    }

    // Filter by range
    const now = Date.now();
    const rangeDays = RANGES.find(r => r.label === range)?.days;
    const rawFiltered = rangeDays
        ? chartdata.filter(([ts]) => ts >= now - rangeDays * 86400000)
        : chartdata;
    const rawSorted = (rawFiltered.length > 0 ? rawFiltered : chartdata).slice().sort((a, b) => a[0] - b[0]);

    // For 1D: bucket by minute (show one price point per minute — latest trade in that minute)
    const bucketByMinute = (data) => {
        const map = {};
        data.forEach(([ts, price]) => {
            const key = Math.floor(ts / 60000) * 60000;
            if (!map[key] || ts > map[key][0]) map[key] = [key, price];
        });
        return Object.values(map).sort((a, b) => a[0] - b[0]);
    };
    const pts = range === '1D' ? bucketByMinute(rawSorted) : rawSorted;

    // SVG dimensions
    const W = 560, H = 200, PAD_L = 64, PAD_R = 16, PAD_T = 16, PAD_B = 36;
    const innerW = W - PAD_L - PAD_R;
    const innerH = H - PAD_T - PAD_B;

    const prices = pts.map(d => d[1]);
    const times  = pts.map(d => d[0]);
    const minP   = Math.min(...prices);
    const maxP   = Math.max(...prices);
    const rangeP = maxP - minP || 1;
    const minT   = Math.min(...times);
    const maxT   = Math.max(...times);
    const rangeT = maxT - minT || 1;

    const toX = (ts) => PAD_L + ((ts - minT) / rangeT) * innerW;
    const toY = (p)  => PAD_T + (1 - (p - minP) / rangeP) * innerH;

    // Build path
    const pathD = pts.map((d, i) => `${i === 0 ? 'M' : 'L'}${toX(d[0]).toFixed(1)},${toY(d[1]).toFixed(1)}`).join(' ');
    // Area fill path
    const areaD = pathD + ` L${toX(times[times.length - 1]).toFixed(1)},${(PAD_T + innerH).toFixed(1)} L${toX(times[0]).toFixed(1)},${(PAD_T + innerH).toFixed(1)} Z`;

    // Y-axis labels (4 ticks)
    const yTicks = [0, 0.33, 0.67, 1].map(f => minP + f * rangeP);

    // X-axis labels (up to 5)
    const xStep = Math.max(1, Math.floor(pts.length / 4));
    const xTicks = pts.filter((_, i) => i % xStep === 0 || i === pts.length - 1);

    // Hover tooltip
    const handleMouseMove = (e) => {
        if (!svgRef.current) return;
        const rect = svgRef.current.getBoundingClientRect();
        const scaleX = W / rect.width;
        const mx = (e.clientX - rect.left) * scaleX;
        // Find nearest point
        let nearest = 0, minDist = Infinity;
        pts.forEach((d, i) => {
            const dist = Math.abs(toX(d[0]) - mx);
            if (dist < minDist) { minDist = dist; nearest = i; }
        });
        setHoverIdx(nearest);
    };

    const hovered = hoverIdx !== null ? pts[hoverIdx] : null;

    return (
                                <div style={{ padding: '12px 10px 16px', overflowX: 'hidden' }}>
            {/* Range selector */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 12, justifyContent: 'flex-end' }}>
                {RANGES.map(r => (
                    <button key={r.label} onClick={() => setRange(r.label)}
                        style={{
                            padding: '4px 12px', border: `1.5px solid ${range === r.label ? DARK : BORDER}`,
                            borderRadius: 6, background: range === r.label ? DARK : 'transparent',
                            color: range === r.label ? GOLD : MUTED, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        }}>
                        {r.label}
                    </button>
                ))}
            </div>

            {/* SVG chart */}
            <svg
                ref={svgRef}
                viewBox={`0 0 ${W} ${H}`}
                style={{ width: '100%', height: 'auto', display: 'block', cursor: 'crosshair' }}
                onMouseMove={handleMouseMove}
                onMouseLeave={() => setHoverIdx(null)}
            >
                <defs>
                    <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={GOLD} stopOpacity="0.25" />
                        <stop offset="100%" stopColor={GOLD} stopOpacity="0.01" />
                    </linearGradient>
                </defs>

                {/* Grid lines */}
                {yTicks.map((p, i) => {
                    const y = toY(p).toFixed(1);
                    return (
                        <g key={i}>
                            <line x1={PAD_L} y1={y} x2={W - PAD_R} y2={y} stroke={BORDER} strokeWidth="0.8" />
                            <text x={PAD_L - 6} y={parseFloat(y) + 4} fontSize="10" fill={MUTED2} textAnchor="end">
                                {Math.round(p).toLocaleString()}
                            </text>
                        </g>
                    );
                })}

                {/* X-axis labels */}
                {xTicks.map((d, i) => {
                    const x = toX(d[0]).toFixed(1);
                    const label = range === '1D' ? moment(d[0]).format('HH:mm') : moment(d[0]).format('MMM D');
                    return (
                        <text key={i} x={x} y={H - 6} fontSize="9.5" fill={MUTED2} textAnchor="middle">
                            {label}
                        </text>
                    );
                })}

                {/* Area fill */}
                <path d={areaD} fill="url(#priceGrad)" />

                {/* Price line */}
                <path d={pathD} fill="none" stroke={GOLD} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

                {/* Hover crosshair + dot */}
                {hovered && (() => {
                    const hx = toX(hovered[0]);
                    const hy = toY(hovered[1]);
                    return (
                        <g>
                            <line x1={hx} y1={PAD_T} x2={hx} y2={PAD_T + innerH} stroke={MUTED2} strokeWidth="1" strokeDasharray="3,3" />
                            <circle cx={hx} cy={hy} r="5" fill={GOLD} stroke={CARD} strokeWidth="2" />
                            {/* Tooltip box */}
                            <rect x={hx > W - 130 ? hx - 126 : hx + 8} y={hy - 28} width={118} height={38} rx="6" fill={DARK} opacity="0.92" />
                            <text x={hx > W - 130 ? hx - 67 : hx + 67} y={hy - 12} fontSize="10" fill={MUTED2} textAnchor="middle">
                                {range === '1D' ? moment(hovered[0]).format('HH:mm') : moment(hovered[0]).format('MMM D, YYYY')}
                            </text>
                            <text x={hx > W - 130 ? hx - 67 : hx + 67} y={hy + 4} fontSize="12" fill={GOLD} fontWeight="700" textAnchor="middle">
                                SAR {Math.round(hovered[1]).toLocaleString()}
                            </text>
                        </g>
                    );
                })()}
            </svg>

            {/* Summary row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 12 }}>
                <span style={{ color: MUTED }}>Low: <strong style={{ color: '#374151' }}>SAR {Math.round(minP).toLocaleString()}</strong></span>
                <span style={{ color: MUTED }}>High: <strong style={{ color: '#374151' }}>SAR {Math.round(maxP).toLocaleString()}</strong></span>
                <span style={{ color: MUTED }}>{pts.length} data points</span>
            </div>
        </div>
    );
};

// ─── News Card ────────────────────────────────────────────────────────────────
const NewsSection = ({ propertyId }) => {
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!propertyId) return;
        setLoading(true);
        axios.get(`${API}/api/news/property/${propertyId}`)
            .then(res => { setNews(res.data?.data || []); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [propertyId]);

    if (loading) return null;
    if (news.length === 0) return null;

    return (
        <SectionCard>
            <CardHeader title="Property News" subtitle={`${news.length} article${news.length !== 1 ? 's' : ''}`} />
            <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {news.map((article, i) => (
                    <div key={article._id} style={{
                        display: 'flex', gap: 14, alignItems: 'flex-start',
                        paddingBottom: i < news.length - 1 ? 10 : 0,
                        borderBottom: i < news.length - 1 ? `1px solid ${BORDER}` : 'none',
                    }}>
                        {article.imageUrl && (
                            <img src={article.imageUrl} alt="" onError={e => e.target.style.display = 'none'}
                                style={{ width: 60, height: 46, objectFit: 'cover', borderRadius: 7, flexShrink: 0 }} />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 11, color: MUTED2, marginBottom: 3 }}>
                                {moment(article.createdAt).format('MMM D, YYYY')}
                            </div>
                            <div style={{ fontSize: 13.5, fontWeight: 700, color: '#111827', marginBottom: 3, lineHeight: 1.35 }}>
                                {article.externalUrl
                                    ? <a href={article.externalUrl} target="_blank" rel="noreferrer" style={{ color: '#111827', textDecoration: 'none' }}>{article.title}</a>
                                    : article.title}
                            </div>
                            <div style={{ fontSize: 12.5, color: MUTED, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                {article.summary}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </SectionCard>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const TradeProperty = () => {
    const dispatch = useDispatch();
    const isMobile = useIsMobile();
    const { t, i18n } = useTranslation();
    const isAr = i18n.language === 'ar';
    const [data, setData]               = useState([]);
    const [blockchain, setBlockchain]   = useState([]);
    const [buytrade, setBuyTrade]       = useState([]);
    const [selltrade, setSellTrade]     = useState([]);
    const [marketdisable, setMarketDisable] = useState(false);
    const [tradeError, setTradeError]   = useState({});
    const [transactions, setTransactions] = useState([]);
    const [chartdata, setChartData]     = useState([]);
    const [activeAction, setActiveAction] = useState('buy');
    const [activeImg, setActiveImg]     = useState(0);
    const [confirmOrder, setConfirmOrder] = useState(null); // holds pending order details
    const [feeSettings, setFeeSettings]   = useState({ exchangeFeePerParty: 0.25, vatRate: 15, minSpread: 10 });
    const [userTokenUnits, setUserTokenUnits] = useState(null); // units owned for this property

    const [openOrders, setOpenOrders]     = useState([]);
    const [editingOrder, setEditingOrder] = useState(null);  // { _id, units, price }
    const [editForm, setEditForm]         = useState({ units: '', price: '' });
    const [editError, setEditError]       = useState('');
    const [editSaving, setEditSaving]     = useState(false);

    const { auth } = useSelector((state) => state);

    const lastPrice  = transactions.length > 0 ? transactions[0].price : null;
    const bestBid    = buytrade.length  > 0 ? buytrade[0].price  : null;
    const bestAsk    = selltrade.length > 0 ? selltrade[0].price : null;
    const spread     = (bestBid && bestAsk) ? (bestAsk - bestBid).toFixed(0) : '—';

    // ── Data fetching ─────────────────────────────────────────────────────────
    async function getTrade() {
        const token = auth?.data?.accesstoken || auth?.data?.access_token;
        const id = window.location.href.split('/')[5];
        const userId = auth?.data?.user?._id;
        const [propRes, bcRes, buyRes, sellRes, chartRes, txRes, feeRes, balRes] = await Promise.all([
            getDataAPIAuth('/get_property/' + id, token),
            getDataAPIAuth('/getPropBlockchainData/' + id, token),
            getDataAPIAuth('/getPropTrade/' + id + '?action=buy', token),
            getDataAPIAuth('/getPropTrade/' + id + '?action=sell', token),
            getDataAPIAuth('/getChartData/' + id, token),
            getDataAPIAuth('/getPropTransaction/' + id, token),
            getDataAPIAuth('/fee-settings', token),
            userId ? getDataAPIAuth('/getUserBalance/' + userId, token) : Promise.resolve({ data: [] }),
        ]);
        setData(Array.isArray(propRes.data) ? propRes.data : (propRes.data ? [propRes.data] : []));
        setBlockchain(Array.isArray(bcRes.data) ? bcRes.data : (bcRes.data ? [bcRes.data] : []));
        setBuyTrade(buyRes.data || []);
        setSellTrade(sellRes.data || []);
        setTransactions(txRes.data || []);
        const chart = (chartRes.data || [])
            .map(d => [d.time, d.price])
            .sort((a, b) => a[0] - b[0]);
        setChartData(chart);
        if (!buyRes.data?.length && !sellRes.data?.length) setMarketDisable(true);
        if (feeRes.data?.settings) setFeeSettings(feeRes.data.settings);
        // Find token balance for this specific property
        const balances = Array.isArray(balRes.data) ? balRes.data : [];
        const propBalance = balances.find(b => String(b.propertyId?._id || b.propertyId) === String(id));
        setUserTokenUnits(propBalance ? propBalance.units : 0);

        // Fetch user's open orders for this property
        if (userId) {
            try {
                const openRes = await getDataAPIAuth(`/getUserOpenOrders/${userId}/${id}`, token);
                setOpenOrders(openRes.data?.orders || []);
            } catch (_) {}
        }
    }

    useEffect(() => { getTrade(); }, [auth]);

    // ── Order submission ──────────────────────────────────────────────────────
    // Step 1: validate and show confirmation modal
    const marketTrade = async (e) => {
        e.preventDefault();
        if (!auth.data) { dialog.error('Login Required', 'Please log in to place an order.'); return; }
        const form = e.target;
        const units = form.elements['units'];
        const price = form.elements['price'];
        const totalTokenSupply = document.querySelector('.totalTokenSupply')?.value;
        if (!units.value || parseInt(units.value) < 1) { dialog.error('Error', 'Please enter a valid number of units.'); return; }
        if (parseInt(units.value) > parseInt(totalTokenSupply)) { dialog.error('Error', 'Units exceed total supply!'); return; }
        // Prevent short selling — sell qty must not exceed owned units
        if (activeAction === 'sell') {
            const ownedUnits = userTokenUnits !== null ? userTokenUnits : 0;
            if (parseInt(units.value) > ownedUnits) {
                dialog.error('Insufficient Units', `You only own ${ownedUnits.toLocaleString()} unit${ownedUnits !== 1 ? 's' : ''} of this property. Short selling is not permitted.`);
                return;
            }
            if (ownedUnits === 0) {
                dialog.error('No Units Owned', 'You do not own any units of this property.');
                return;
            }
        }

        const priceTypeRadios = form.querySelectorAll('input[name="priceType"]');
        let priceTypeValue = 'marketPrice';
        priceTypeRadios.forEach(r => { if (r.checked) priceTypeValue = r.value; });

        // ── Minimum spread validation (limit orders only) ──────────────────
        // BUG FIX #1: Only enforce spread when the order would NOT immediately match.
        // If a sell price <= bestBid, it will execute as a trade. If buy price >= bestAsk, it will execute.
        if (priceTypeValue === 'customPrice' && price && price.value) {
            const minSpread = feeSettings.minSpread != null ? feeSettings.minSpread : 10;
            const enteredPrice = parseInt(price.value);
            if (activeAction === 'buy' && bestAsk) {
                // If buy price >= bestAsk, it will match immediately — no spread concern
                if (enteredPrice < bestAsk) {
                    const maxAllowedBid = bestAsk - minSpread;
                    if (enteredPrice > maxAllowedBid) {
                        dialog.error('Price Too Close to Ask', `Your buy price (﷼ ${Number(enteredPrice || 0).toLocaleString("en-US")}) is too close to the best ask (﷼ ${Number(bestAsk || 0).toLocaleString("en-US")}). Maximum allowed bid is ﷼ ${Number(maxAllowedBid || 0).toLocaleString("en-US")} to maintain the minimum spread of ﷼ ${minSpread}.`);
                        return;
                    }
                }
            }
            if (activeAction === 'sell' && bestBid) {
                // If sell price <= bestBid, it will match immediately — no spread concern
                if (enteredPrice > bestBid) {
                    const minAllowedAsk = bestBid + minSpread;
                    if (enteredPrice < minAllowedAsk) {
                        dialog.error('Price Too Close to Bid', `Your sell price (﷼ ${Number(enteredPrice || 0).toLocaleString("en-US")}) is too close to the best bid (﷼ ${Number(bestBid || 0).toLocaleString("en-US")}). Minimum allowed ask is ﷼ ${Number(minAllowedAsk || 0).toLocaleString("en-US")} to maintain the minimum spread of ﷼ ${minSpread}.`);
                        return;
                    }
                }
            }
        }

        const marketPriceVal = activeAction === 'buy' ? (bestAsk || lastPrice || 0) : (bestBid || lastPrice || 0);
        const limitPriceVal  = price ? price.value : '0';
        const displayPrice   = priceTypeValue === 'marketPrice' ? marketPriceVal : limitPriceVal;
        const unitsVal       = parseInt(units.value);
        const estimatedTotal = displayPrice && unitsVal ? unitsVal * parseInt(displayPrice) : null;

        // Calculate fee for display
        const feeRate      = (feeSettings.exchangeFeePerParty || 0.25) / 100;
        const vatRate      = (feeSettings.vatRate || 15) / 100;
        const feeSubtotal  = estimatedTotal ? Math.round(estimatedTotal * feeRate * 100) / 100 : null;
        const feeVat       = feeSubtotal ? Math.round(feeSubtotal * vatRate * 100) / 100 : null;
        const feeTotal     = feeSubtotal ? Math.round((feeSubtotal + feeVat) * 100) / 100 : null;
        const netTotal     = estimatedTotal && feeTotal
            ? (activeAction === 'buy' ? estimatedTotal + feeTotal : estimatedTotal - feeTotal)
            : estimatedTotal;

        // Show confirmation modal — actual submit happens in confirmAndPlace
        setConfirmOrder({
            propertyId: form.elements['propertyId'].value,
            userId: auth.data.user._id,
            units: units.value,
            priceType: priceTypeValue,
            marketPrice: String(marketPriceVal),
            price: limitPriceVal,
            action: activeAction,
            // display-only
            displayPrice,
            estimatedTotal,
            feeSubtotal,
            feeVat,
            feeTotal,
            netTotal,
            feeRate: feeSettings.exchangeFeePerParty || 0.25,
            tokenName: form.elements['unitName']?.value || '',
            symbol: form.elements['unitId']?.value || '',
        });
    };

    // Step 2: user confirmed — actually submit
    const confirmAndPlace = () => {
        if (!confirmOrder) return;
        const { displayPrice: _d, estimatedTotal: _e, tokenName: _n, symbol: _s, ...details } = confirmOrder;
        const token = auth?.data?.accesstoken || auth?.data?.access_token;
        setConfirmOrder(null);
        postDataAPI('trade', details, token)
            .then(res => {
                if (res.data.status === 0) {
                    const errors = res.data.errors || {};
                    setTradeError(errors);
                    if (errors.price?.toLowerCase().includes('balance')) {
                        dialog.error('Insufficient Balance', "You don't have enough balance.");
                    } else if (errors.units?.toLowerCase().includes('unit') || errors.units?.toLowerCase().includes("don't")) {
                        dialog.error('Insufficient Units', errors.units);
                    } else if (errors.priceType?.toLowerCase().includes('counterparty') || errors.priceType?.toLowerCase().includes('no buy') || errors.priceType?.toLowerCase().includes('no sell')) {
                        dialog.error('No Counterparty Available', errors.priceType);
                    } else {
                        dialog.error('Order Failed', Object.values(errors).filter(Boolean).join(' ') || 'Please check your order details.');
                    }
                } else if (res.data.status === -1) {
                    getTrade(); setTradeError(res.data.errors || {}); dialog.error('Partial Success', res.data.message);
                } else {
                    getTrade(); setTradeError({}); dialog.success('Order Placed', res.data.message);
                }
            })
            .catch(() => dialog.error('Error', 'Something went wrong. Please try again.'));
    };

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div style={{ background: BG, minHeight: '100vh', fontFamily: "'Inter', sans-serif" }}>

            {/* ── Order Confirmation Modal ─────────────────────────────── */}
            {confirmOrder && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 9999,
                    background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: 20,
                }}>
                    <div style={{
                        background: CARD, borderRadius: 18, width: '100%', maxWidth: 420,
                        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                        overflow: 'hidden',
                    }}>
                        {/* Modal header */}
                        <div style={{
                            background: confirmOrder.action === 'buy'
                                ? 'linear-gradient(135deg, #1a3c2e, #1a5c3a)'
                                : 'linear-gradient(135deg, #7f1d1d, #b91c1c)',
                            padding: '20px 24px',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}>
                            <div>
                                <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
                                    Confirm Order
                                </div>
                                <div style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>
                                    {confirmOrder.action === 'buy' ? '▲ Buy Order' : '▼ Sell Order'}
                                </div>
                            </div>
                            <button onClick={() => setConfirmOrder(null)} style={{
                                background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8,
                                color: '#fff', width: 34, height: 34, fontSize: 18, cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>✕</button>
                        </div>

                        {/* Order summary rows */}
                        <div style={{ padding: '20px 24px' }}>
                            {[
                                { label: 'Token',        value: `${confirmOrder.tokenName} (${confirmOrder.symbol})` },
                                { label: 'Action',       value: confirmOrder.action === 'buy' ? 'Buy' : 'Sell', color: confirmOrder.action === 'buy' ? GREEN : RED },
                                { label: 'Units',        value: `${parseInt(confirmOrder.units).toLocaleString()} units` },
                                { label: 'Price Type',   value: confirmOrder.priceType === 'marketPrice' ? 'Market Price' : 'Limit Price' },
                                { label: 'Price / Unit', value: confirmOrder.displayPrice ? fmt(confirmOrder.displayPrice) : '—' },
                                { label: 'Subtotal',     value: confirmOrder.estimatedTotal ? fmt(confirmOrder.estimatedTotal) : '—' },
                                { label: `Exchange Fee (${confirmOrder.feeRate}% + 15% VAT)`, value: confirmOrder.feeTotal ? fmt(confirmOrder.feeTotal) : '—', color: MUTED },
                                { label: confirmOrder.action === 'buy' ? 'Total You Pay' : 'Total You Receive', value: confirmOrder.netTotal ? fmt(confirmOrder.netTotal) : '—', bold: true },
                            ].map(({ label, value, color, bold }) => (
                                <div key={label} style={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    padding: '10px 0', borderBottom: `1px solid ${BORDER}`,
                                }}>
                                    <span style={{ fontSize: 13, color: MUTED }}>{label}</span>
                                    <span style={{ fontSize: 13.5, fontWeight: bold ? 700 : 600, color: color || '#111827' }}>{value}</span>
                                </div>
                            ))}

                            {/* Warning note */}
                            <div style={{
                                marginTop: 16, padding: '10px 14px',
                                background: confirmOrder.action === 'buy' ? '#f0fdf4' : '#fef2f2',
                                borderRadius: 8, border: `1px solid ${confirmOrder.action === 'buy' ? '#bbf7d0' : '#fecaca'}`,
                                fontSize: 12.5, color: confirmOrder.action === 'buy' ? '#166534' : '#991b1b',
                                lineHeight: 1.5,
                            }}>
                                {confirmOrder.action === 'buy'
                                    ? '⚠ Market orders execute at the best available ask price. Final fill price may differ if the order book changes.'
                                    : '⚠ Market orders execute at the best available bid price. Final fill price may differ if the order book changes.'}
                            </div>

                            {/* Action buttons */}
                            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                                <button onClick={() => setConfirmOrder(null)} style={{
                                    flex: 1, padding: '12px', border: `1.5px solid ${BORDER}`,
                                    borderRadius: 10, background: '#fff', fontSize: 14, fontWeight: 600,
                                    color: '#374151', cursor: 'pointer',
                                }}>Cancel</button>
                                <button onClick={confirmAndPlace} style={{
                                    flex: 2, padding: '12px', border: 'none', borderRadius: 10,
                                    background: confirmOrder.action === 'buy'
                                        ? 'linear-gradient(135deg, #1a3c2e, #1a5c3a)'
                                        : 'linear-gradient(135deg, #7f1d1d, #b91c1c)',
                                    color: confirmOrder.action === 'buy' ? GOLD : '#fff',
                                    fontSize: 14, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.3px',
                                }}>
                                    {confirmOrder.action === 'buy' ? '▲ Confirm Buy' : '▼ Confirm Sell'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {data.map((property) => {
                const images = (property.imageName && Array.isArray(property.imageName) && property.imageName.length > 0)
                    ? property.imageName : ['/img/al_narjes.jpg'];
                const isLocked = property.tokenSupply < property.totalTokenSupply;
                const progress = property.totalTokenSupply > 0
                    ? Math.round((property.tokenSupply / property.totalTokenSupply) * 100) : 0;
                const bc = blockchain[0];

                return (
                    <div key={property._id} style={{ maxWidth: 1280, margin: '0 auto', padding: isMobile ? '16px 12px 48px' : '24px 20px 48px', boxSizing: 'border-box', width: '100%', overflowX: 'hidden' }}>

                        {/* ── Header bar ────────────────────────────────────── */}
                        <div style={{ marginBottom: 20 }}>
                            <div style={{ fontSize: 12, color: MUTED2, marginBottom: 6 }}>
                                <a href="/dashboard" style={{ color: MUTED2, textDecoration: 'none' }}>Dashboard</a>
                                <span style={{ margin: '0 6px' }}>›</span>
                                <span style={{ color: DARK, fontWeight: 600 }}>{property.title}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexDirection: isAr ? 'row-reverse' : 'row', flexWrap: 'wrap', gap: isMobile ? 8 : 12 }}>
                                <div>
                                            <h1 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, color: DARK, margin: '0 0 4px' }}>{property.title}</h1>
                                    <p style={{ fontSize: 13, color: MUTED, margin: 0 }}>📍 {property.address}</p>
                                </div>
                                {/* Live stat pills */}
                                <div style={{ display: 'flex', gap: isMobile ? 14 : 28, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <StatPill label={isAr ? 'آخر سعر' : 'Last Price'}  value={lastPrice  ? fmt(lastPrice)  : '—'} valueColor={DARK} />
                                    <StatPill label={isAr ? 'أفضل عرض' : 'Best Bid'}    value={bestBid    ? fmt(bestBid)    : '—'} valueColor={GREEN} />
                                    <StatPill label={isAr ? 'أفضل طلب' : 'Best Ask'}    value={bestAsk    ? fmt(bestAsk)    : '—'} valueColor={RED} />
                                    <StatPill label={isAr ? 'الفارق' : 'Spread'}      value={spread !== '—' ? `﷼ ${Number(spread || 0).toLocaleString("en-US")}` : '—'} valueColor={MUTED} />
                                    <StatPill label={isAr ? 'سعر الوحدة' : 'Token Price'} value={fmt(property.tokenPrice)} valueColor={GOLD} />
                                </div>
                            </div>
                        </div>

                        {/* ── IPO lock banner ───────────────────────────────── */}
                        {isLocked && (
                            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#92400e', fontWeight: 600 }}>
                                <span>⏳</span>
                                <span>Subscription in progress ({progress}% filled) — Exchange trading opens once fully subscribed.</span>
                            </div>
                        )}

                           {/* ── Main 3-column grid ──────────────────── */}
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '320px 1fr 300px', gap: 16, alignItems: 'start', width: '100%', boxSizing: 'border-box' }}>

                            {/* ── Col 1: Image + Property Details ───────────── */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, order: isMobile ? 1 : 'unset', minWidth: 0 }}>

                                {/* Image carousel */}
                                <SectionCard>
                                    <div style={{ position: 'relative', aspectRatio: '4/3', background: '#111', overflow: 'hidden' }}>
                                        <img src={images[activeImg]} alt={property.title}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            onError={e => { e.target.src = '/img/al_narjes.jpg'; }} />
                                        {images.length > 1 && (
                                            <>
                                                <button onClick={() => setActiveImg(i => (i - 1 + images.length) % images.length)}
                                                    style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%', width: 32, height: 32, color: '#fff', cursor: 'pointer', fontSize: 16 }}>‹</button>
                                                <button onClick={() => setActiveImg(i => (i + 1) % images.length)}
                                                    style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%', width: 32, height: 32, color: '#fff', cursor: 'pointer', fontSize: 16 }}>›</button>
                                                <div style={{ position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 5 }}>
                                                    {images.map((_, i) => (
                                                        <button key={i} onClick={() => setActiveImg(i)}
                                                            style={{ width: 7, height: 7, borderRadius: '50%', border: 'none', background: i === activeImg ? GOLD : 'rgba(255,255,255,0.5)', cursor: 'pointer', padding: 0 }} />
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </SectionCard>

                                {/* Property stats */}
                                <SectionCard>
                                    <CardHeader title={isAr ? 'تفاصيل العقار' : 'Property Details'} isAr={isAr} />
                                    <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                                        {[
                                            { label: isAr ? 'قيمة العقار' : 'Property Value',  value: fmt(property.propertyEstimatedValue) },
                                            { label: isAr ? 'نسبة الطرح' : 'Offering',        value: `${property.percentageOfOwnership || 0}%` },
                                            { label: isAr ? 'إجمالي الوحدات' : 'Total Tokens',    value: (property.totalTokenSupply || 0).toLocaleString() },
                                            { label: isAr ? 'الوحدات المباعة' : 'Tokens Sold',     value: (property.tokenSupply || 0).toLocaleString() },
                                        ].map((s, i) => (
                                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', flexDirection: isAr ? 'row-reverse' : 'row', fontSize: 13, borderBottom: i < 3 ? `1px solid ${BORDER}` : 'none', paddingBottom: i < 3 ? 10 : 0 }}>
                                                <span style={{ color: MUTED }}>{s.label}</span>
                                                <span style={{ fontWeight: 700, color: '#111827' }}>{s.value}</span>
                                            </div>
                                        ))}
                                    </div>
                                </SectionCard>

                                {/* Property News (property-specific only) */}
                                <NewsSection propertyId={property._id} />
                            </div>

                            {/* ── Col 2: Order Book + Chart + Transactions ───── */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, order: isMobile ? 3 : 'unset', minWidth: 0 }}>

                                {/* Order Book */}
                                <SectionCard>
                                    <CardHeader title={isAr ? 'دفتر الأوامر' : 'Order Book'} subtitle={`${isAr ? 'الفارق' : 'Spread'}: ${spread !== '—' ? `﷼ ${Number(spread || 0).toLocaleString('en-US')}` : '—'}`} isAr={isAr} />
                                    {/* Header row */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', padding: '10px 16px', borderBottom: `1px solid ${BORDER}`, background: '#fafafa' }}>
                                        {(isAr ? ['الكمية', 'الطلب', 'العرض', 'الكمية'] : ['QTY', 'BID', 'ASK', 'QTY']).map((h, i) => (
                                            <span key={i} style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: 'center', color: i === 1 ? GREEN : i === 2 ? RED : MUTED2 }}>{h}</span>
                                        ))}
                                    </div>
                                    {/* Scrollable rows — 5 visible */}
                                    <div style={{ maxHeight: 175, overflowY: 'auto' }}>
                                        {(buytrade.length === 0 && selltrade.length === 0) ? (
                                            <div style={{ textAlign: 'center', padding: '24px', color: MUTED2, fontSize: 13 }}>Order book is empty</div>
                                        ) : (
                                            Array.from({ length: Math.max(buytrade.length, selltrade.length) }).map((_, i) => {
                                                const bid = buytrade[i];
                                                const ask = selltrade[i];
                                                return (
                                                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', padding: '8px 16px', borderBottom: `1px solid ${BORDER}`, background: i % 2 === 0 ? CARD : '#fafafa' }}>
                                                        <span style={{ fontSize: 13, textAlign: 'center', color: '#374151' }}>{bid ? `${bid.units}` : '—'}</span>
                                                        <span style={{ fontSize: 13, fontWeight: 600, textAlign: 'center', color: bid ? GREEN : MUTED2 }}>{bid ? <><SARAmount value={bid.price} decimals={0} /></> : '—'}</span>
                                                        <span style={{ fontSize: 13, fontWeight: 600, textAlign: 'center', color: ask ? RED : MUTED2, borderLeft: `1px solid ${BORDER}` }}>{ask ? <><SARAmount value={ask.price} decimals={0} /></> : '—'}</span>
                                                        <span style={{ fontSize: 13, textAlign: 'center', color: '#374151' }}>{ask ? `${ask.units}` : '—'}</span>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </SectionCard>

                                {/* Price Chart (custom SVG — no volume) */}
                                <SectionCard>
                                    <CardHeader title={isAr ? 'مخطط السعر' : 'Price Chart'} subtitle={lastPrice ? `${isAr ? 'آخر سعر' : 'Last'}: ﷼ ${Number(lastPrice || 0).toLocaleString('en-US')}` : undefined} isAr={isAr} />
                                    <PriceChart chartdata={chartdata} />
                                </SectionCard>

                                {/* My Open Orders */}
                                <SectionCard>
                                    <CardHeader title={isAr ? 'أوامري المفتوحة' : 'My Open Orders'} subtitle={openOrders.length > 0 ? `${openOrders.length} ${isAr ? 'نشط' : 'active'}` : undefined} isAr={isAr} />
                                    {openOrders.length === 0 ? (
                                        <div style={{ padding: '20px', textAlign: 'center', color: MUTED2, fontSize: 13 }}>
                                            No open orders for this property
                                        </div>
                                    ) : (
                                        <div style={{ overflowX: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                                <thead>
                                                    <tr style={{ background: '#f9fafb' }}>
                                        {(isAr ? ['الإجراء', 'الكمية', 'السعر', 'النوع', 'التاريخ', 'خيارات'] : ['Side', 'Units', 'Price', 'Type', 'Date', 'Actions']).map((h, idx) => (
                                            <th key={h} style={{ padding: '9px 14px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: MUTED2, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: `1px solid ${BORDER}` }}>{h}</th>
                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {openOrders.map((order, i) => (
                                                        <React.Fragment key={order._id}>
                                                            <tr style={{ borderBottom: `1px solid ${BORDER}`, background: editingOrder?._id === order._id ? '#f0fdf4' : 'transparent' }}>
                                                                <td style={{ padding: '10px 14px' }}>
                                                                    <span style={{ fontWeight: 700, color: order.action === 'buy' ? GREEN : RED, fontSize: 12, textTransform: 'uppercase' }}>
                                                                        {order.action === 'buy' ? '▲ Buy' : '▼ Sell'}
                                                                    </span>
                                                                </td>
                                                                <td style={{ padding: '10px 14px', fontWeight: 600 }}>{order.units}</td>
                                                                <td style={{ padding: '10px 14px', fontWeight: 600 }}>
                                                                    <SARAmount value={order.price} decimals={0} />
                                                                </td>
                                                                <td style={{ padding: '10px 14px', color: MUTED, fontSize: 12 }}>
                                                                    {order.priceType === 'marketPrice' ? 'Market' : 'Limit'}
                                                                </td>
                                                                <td style={{ padding: '10px 14px', color: MUTED, fontSize: 12 }}>
                                                                    {moment(order.createdAt).format('MMM D, HH:mm')}
                                                                </td>
                                                                <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                                                                    <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                                                                        <button
                                                                            onClick={() => {
                                                                                if (editingOrder?._id === order._id) { setEditingOrder(null); setEditError(''); }
                                                                                else { setEditingOrder(order); setEditForm({ units: order.units, price: order.price }); setEditError(''); }
                                                                            }}
                                                                            style={{ padding: '5px 12px', borderRadius: 6, border: `1px solid ${DARK}`, background: 'transparent', color: DARK, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                                                                        >
                                                                            {editingOrder?._id === order._id ? 'Close' : 'Edit'}
                                                                        </button>
                                                                        <button
                                                                            onClick={async () => {
                                                                                if (!window.confirm('Cancel this order?')) return;
                                                                                const token = auth?.data?.accesstoken || auth?.data?.access_token;
                                                                                const userId = auth?.data?.user?._id;
                                                                                try {
                                                                                    const res = await axios.delete(`${API}/api/cancelOrder/${order._id}`, {
                                                                                        headers: { Authorization: `Bearer ${token}` },
                                                                                        data: { userId }
                                                                                    });
                                                                                    if (res.data.status === 1) { getTrade(); dialog.success('Cancelled', 'Order cancelled successfully.'); }
                                                                                    else dialog.error('Error', res.data.msg || 'Failed to cancel order.');
                                                                                } catch { dialog.error('Error', 'Failed to cancel order.'); }
                                                                            }}
                                                                            style={{ padding: '5px 12px', borderRadius: 6, border: `1px solid ${RED}`, background: 'transparent', color: RED, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                                                                        >
                                                                            Cancel
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                            {/* Inline edit row */}
                                                            {editingOrder?._id === order._id && (
                                                                <tr style={{ background: '#f0fdf4', borderBottom: `1px solid ${BORDER}` }}>
                                                                    <td colSpan={6} style={{ padding: '12px 14px' }}>
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                                                                <label style={{ fontSize: 11, fontWeight: 600, color: MUTED2, textTransform: 'uppercase' }}>Units</label>
                                                                                <input type="number" min="1"
                                                                                    max={order.action === 'sell' ? (userTokenUnits || undefined) : undefined}
                                                                                    value={editForm.units}
                                                                                    onChange={e => setEditForm(f => ({ ...f, units: e.target.value }))}
                                                                                    style={{ width: 90, padding: '6px 10px', border: `1.5px solid ${BORDER}`, borderRadius: 7, fontSize: 13, outline: 'none' }} />
                                                                            </div>
                                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                                                                <label style={{ fontSize: 11, fontWeight: 600, color: MUTED2, textTransform: 'uppercase' }}>Price (SAR)</label>
                                                                                <input type="number" min="1"
                                                                                    value={editForm.price}
                                                                                    onChange={e => setEditForm(f => ({ ...f, price: e.target.value }))}
                                                                                    style={{ width: 110, padding: '6px 10px', border: `1.5px solid ${BORDER}`, borderRadius: 7, fontSize: 13, outline: 'none' }} />
                                                                            </div>
                                                                            {editError && <span style={{ fontSize: 12, color: RED }}>{editError}</span>}
                                                                            <button
                                                                                disabled={editSaving}
                                                                                onClick={async () => {
                                                                                    const newUnits = parseInt(editForm.units);
                                                                                    const newPrice = parseInt(editForm.price);
                                                                                    if (!newUnits || newUnits < 1) { setEditError('Units must be at least 1.'); return; }
                                                                                    if (!newPrice || newPrice < 1) { setEditError('Price must be at least 1.'); return; }
                                                                                    if (order.action === 'sell' && userTokenUnits !== null && newUnits > userTokenUnits) {
                                                                                        setEditError(`You only own ${userTokenUnits} units.`); return;
                                                                                    }
                                                                                    // Minimum spread check — skip if order would match immediately
                                                                                    const minSpr = feeSettings.minSpread != null ? feeSettings.minSpread : 10;
                                                                                    if (order.action === 'buy' && bestAsk) {
                                                                                        // If buy price >= bestAsk, it would match — no spread concern
                                                                                        if (newPrice < bestAsk) {
                                                                                            const maxBid = bestAsk - minSpr;
                                                                                            if (newPrice > maxBid) { setEditError(`Max allowed buy price is ﷼ ${Number(maxBid || 0).toLocaleString("en-US")} (min spread SAR ${minSpr}).`); return; }
                                                                                        }
                                                                                    }
                                                                                    if (order.action === 'sell' && bestBid) {
                                                                                        // If sell price <= bestBid, it would match — no spread concern
                                                                                        if (newPrice > bestBid) {
                                                                                            const minAsk = bestBid + minSpr;
                                                                                            if (newPrice < minAsk) { setEditError(`Min allowed sell price is ﷼ ${Number(minAsk || 0).toLocaleString("en-US")} (min spread SAR ${minSpr}).`); return; }
                                                                                        }
                                                                                    }
                                                                                    setEditSaving(true); setEditError('');
                                                                                    const token = auth?.data?.accesstoken || auth?.data?.access_token;
                                                                                    const userId = auth?.data?.user?._id;
                                                                                    try {
                                                                                        const res = await axios.put(`${API}/api/updateOrder/${order._id}`, { userId, units: newUnits, price: newPrice }, { headers: { Authorization: `Bearer ${token}` } });
                                                                                        if (res.data.status === 1) { setEditingOrder(null); getTrade(); dialog.success('Updated', 'Order updated successfully.'); }
                                                                                        else setEditError(res.data.msg || 'Update failed.');
                                                                                    } catch { setEditError('Update failed. Please try again.'); }
                                                                                    finally { setEditSaving(false); }
                                                                                }}
                                                                                style={{ padding: '7px 18px', borderRadius: 7, background: DARK, color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: editSaving ? 'not-allowed' : 'pointer', opacity: editSaving ? 0.7 : 1 }}
                                                                            >
                                                                                {editSaving ? 'Saving…' : 'Save Changes'}
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </React.Fragment>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </SectionCard>
                            </div>

                            {/* ── Col 3: Order Form ──────────────────────────── */}
                            <div style={{ position: isMobile ? 'static' : 'sticky', top: 24, display: 'flex', flexDirection: 'column', gap: 14, order: isMobile ? 2 : 'unset', minWidth: 0 }}>

                                <SectionCard>
                                    <CardHeader title={isAr ? 'تقديم أمر' : 'Place Order'} isAr={isAr} />
                                    <div style={{ padding: isMobile ? '14px 14px' : '18px 20px' }}>
                                        <input type="hidden" className="totalTokenSupply" value={property.totalTokenSupply} />

                                        {/* Buy / Sell toggle */}
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, background: '#f3f4f6', borderRadius: 10, padding: 4, marginBottom: 20 }}>
                                            {['buy', 'sell'].map(action => {
                                                const noTokensForSell = action === 'sell' && (userTokenUnits === null || userTokenUnits <= 0);
                                                const btnDisabled = isLocked || noTokensForSell;
                                                return (
                                                <button key={action} type="button"
                                                    disabled={btnDisabled}
                                                    onClick={() => !btnDisabled && setActiveAction(action)}
                                                    title={noTokensForSell ? (isAr ? 'ليس لديك وحدات لهذا العقار' : 'You have no tokens for this property') : undefined}
                                                    style={{
                                                        padding: '10px 0', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700,
                                                        cursor: btnDisabled ? 'not-allowed' : 'pointer',
                                                        background: activeAction === action ? (action === 'buy' ? GREEN : RED) : 'transparent',
                                                        color: activeAction === action ? '#fff' : (noTokensForSell ? '#d1d5db' : MUTED),
                                                        opacity: btnDisabled ? 0.45 : 1,
                                                        transition: 'all 0.15s ease',
                                                    }}>
                                                    {action === 'buy' ? (isAr ? '▲ شراء' : '▲ Buy') : (isAr ? '▼ بيع' : '▼ Sell')}
                                                </button>
                                                );
                                            })}
                                        </div>

                                        <form className="marketTrade" onSubmit={marketTrade}>
                                            <input type="hidden" name="propertyId" value={bc?.propertyId || ''} />
                                            <input type="hidden" name="marketPrice" value={activeAction === 'buy' ? (bestAsk || lastPrice || 0) : (bestBid || lastPrice || 0)} />
                                            <input type="hidden" name="action" value={activeAction} />

                                            {/* Token info row */}
                                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 70px' : '1fr 80px', gap: 8, marginBottom: 14 }}>
                                                <div>
                                                    <label style={{ fontSize: 11, fontWeight: 600, color: MUTED2, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 5, textAlign: isAr ? 'right' : 'left' }}>{isAr ? 'اسم الوحدة' : 'Token Name'}</label>
                                                    <input type="text" name="unitName" readOnly value={bc?.contractName || ''} onChange={() => {}}
                                                        style={{ width: '100%', padding: '9px 12px', border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: 13, background: '#f9fafb', color: '#374151', boxSizing: 'border-box' }} />
                                                </div>
                                                <div>
                                                    <label style={{ fontSize: 11, fontWeight: 600, color: MUTED2, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 5, textAlign: isAr ? 'right' : 'left' }}>{isAr ? 'الرمز' : 'Symbol'}</label>
                                                    <input type="text" name="unitId" readOnly value={bc?.symbol || ''} onChange={() => {}}
                                                        style={{ width: '100%', padding: '9px 12px', border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: 13, background: '#f9fafb', color: '#374151', boxSizing: 'border-box' }} />
                                                </div>
                                            </div>

                                            {/* Units */}
                                            <div style={{ marginBottom: 14 }}>
                                                <label style={{ fontSize: 11, fontWeight: 600, color: MUTED2, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 5, textAlign: isAr ? 'right' : 'left' }}>{isAr ? 'الكمية' : 'Units'}</label>
                                                 <input type="number" min="1"
                                                     max={activeAction === 'sell' && userTokenUnits !== null ? userTokenUnits : undefined}
                                                     name="units" placeholder="Enter number of units"
                                                     style={{ width: '100%', padding: '10px 12px', border: `1.5px solid ${tradeError?.units ? RED : BORDER}`, borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                                                {tradeError?.units && <span style={{ fontSize: 11.5, color: RED, marginTop: 4, display: 'block' }}>{tradeError.units}</span>}
                                            </div>

                                            {/* Price type */}
                                            <div style={{ marginBottom: 18 }}>
                                                <label style={{ fontSize: 11, fontWeight: 600, color: MUTED2, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 8, textAlign: isAr ? 'right' : 'left' }}>{isAr ? 'نوع السعر' : 'Price Type'}</label>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                    <label style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 10, cursor: 'pointer', padding: isMobile ? '8px 10px' : '10px 12px', border: `1.5px solid ${BORDER}`, borderRadius: 8, fontSize: 13, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                                                        <input type="radio" name="priceType" value="customPrice"
                                                            defaultChecked={marketdisable}
                                                            style={{ accentColor: DARK }} />
                                                        <span style={{ fontWeight: 600, color: '#374151' }}>{isAr ? 'سعر محدد' : 'Limit Price'}</span>
                                                        <input type="number" min="1" name="price" placeholder="﷼ Enter price"
                                                            style={{ marginLeft: isAr ? 0 : (isMobile ? 0 : 'auto'), marginRight: isAr ? (isMobile ? 0 : 'auto') : 0, width: isMobile ? '100%' : 110, padding: '6px 10px', border: `1px solid ${BORDER}`, borderRadius: 6, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                                                    </label>
                                                    {tradeError?.price && <span style={{ fontSize: 11.5, color: RED }}>{tradeError.price}</span>}
                                                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: marketdisable ? 'not-allowed' : 'pointer', padding: '10px 12px', border: `1.5px solid ${BORDER}`, borderRadius: 8, fontSize: 13, opacity: marketdisable ? 0.5 : 1 }}>
                                                        <input type="radio" name="priceType" value="marketPrice"
                                                            defaultChecked={!marketdisable}
                                                            disabled={marketdisable}
                                                            style={{ accentColor: DARK }} />
                                                        <span style={{ fontWeight: 600, color: '#374151' }}>{isAr ? 'سعر السوق' : 'Market Price'}</span>
                                                        <span style={{ marginLeft: isAr ? 0 : 'auto', marginRight: isAr ? 'auto' : 0, fontSize: 13, fontWeight: 700, color: GOLD }}>
                                                            {activeAction === 'buy'
                                                                ? (bestAsk ? fmt(bestAsk) : (lastPrice ? fmt(lastPrice) : '—'))
                                                                : (bestBid ? fmt(bestBid) : (lastPrice ? fmt(lastPrice) : '—'))}
                                                        </span>
                                                    </label>
                                                </div>
                                            </div>

                                            {/* Submit */}
                                            {(() => {
                                                const noTokensToSell = activeAction === 'sell' && (userTokenUnits === null || userTokenUnits <= 0);
                                                const submitDisabled = isLocked || noTokensToSell;
                                                return (
                                                <button type="submit" disabled={submitDisabled}
                                                    style={{
                                                        width: '100%', padding: '13px', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700,
                                                        cursor: submitDisabled ? 'not-allowed' : 'pointer',
                                                        background: submitDisabled ? '#e5e7eb' : (activeAction === 'buy' ? `linear-gradient(135deg, ${DARK}, #1a5c3a)` : `linear-gradient(135deg, #7f1d1d, #b91c1c)`),
                                                        color: submitDisabled ? MUTED2 : (activeAction === 'buy' ? GOLD : '#fff'),
                                                        opacity: submitDisabled ? 0.6 : 1,
                                                        letterSpacing: '0.5px',
                                                    }}>
                                                    {isLocked
                                                        ? (isAr ? '🔒 التداول موقوف' : '🔒 Trading Locked')
                                                        : noTokensToSell
                                                            ? (isAr ? '⚠ لا توجد وحدات للبيع' : '⚠ No tokens to sell')
                                                            : (activeAction === 'buy' ? (isAr ? '▲ تقديم أمر شراء' : '▲ Place Buy Order') : (isAr ? '▼ تقديم أمر بيع' : '▼ Place Sell Order'))}
                                                </button>
                                                );
                                            })()}
                                        </form>

                                        {/* Balance info */}
                                        <div style={{ marginTop: 14, padding: '12px 14px', background: '#f9fafb', borderRadius: 8, fontSize: 12.5 }}>
                                            {activeAction === 'sell' ? (
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                                                         <span>{isAr ? 'الوحدات المتاحة' : 'Units Available'}</span>
                                                    <span style={{ fontWeight: 700, color: DARK }}>
                                                        {userTokenUnits !== null ? `${userTokenUnits.toLocaleString()} ${isAr ? 'وحدة' : 'units'}` : '—'}
                                                    </span>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                                    <span style={{ color: MUTED }}>{isAr ? 'رصيدك' : 'Your Balance'}</span>
                                                    <span style={{ fontWeight: 700, color: DARK }}>{fmt(auth.data?.user?.totalBalance || 0)}</span>
                                                </div>
                                            )}
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                                <span style={{ color: MUTED }}>{isAr ? 'رسوم التداول' : 'Exchange Fee (per party)'}</span>
                                                <span style={{ fontWeight: 600, color: MUTED }}>{feeSettings.exchangeFeePerParty || 0.25}% + VAT</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <span style={{ color: MUTED }}>{isAr ? 'نسبة ضريبة القيمة المضافة' : 'VAT Rate'}</span>
                                                <span style={{ fontWeight: 600, color: MUTED }}>{feeSettings.vatRate || 15}%</span>
                                            </div>
                                        </div>
                                    </div>
                                </SectionCard>

                                {/* Latest Transactions */}
                                <SectionCard>
                                    <CardHeader title={isAr ? 'آخر الصفقات' : 'Latest Transactions'} isAr={isAr} />
                                    <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                                        {transactions.length === 0 ? (
                                            <div style={{ textAlign: 'center', padding: '24px', color: MUTED2, fontSize: 13 }}>No transactions yet</div>
                                        ) : (
                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                <thead>
                                                    <tr style={{ background: '#fafafa' }}>
                                        {(isAr ? ['السعر', 'الكمية', 'التاريخ', 'الوقت'] : ['Price', 'Units', 'Date', 'Time']).map(h => (
                                            <th key={h} style={{ padding: '10px 16px', fontSize: 11, fontWeight: 700, color: MUTED2, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center', borderBottom: `1px solid ${BORDER}` }}>{h}</th>
                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {transactions.map((t, i) => (
                                                        <tr key={i} style={{ borderBottom: `1px solid ${BORDER}`, background: i % 2 === 0 ? CARD : '#fafafa' }}>
                                                            <td style={{ padding: '8px 16px', fontSize: 13, fontWeight: 600, color: DARK, textAlign: 'center' }}><SARAmount value={t.price || '—'} decimals={0} /></td>
                                                            <td style={{ padding: '8px 16px', fontSize: 13, color: '#374151', textAlign: 'center' }}>{t.units}</td>
                                                            <td style={{ padding: '8px 16px', fontSize: 12, color: MUTED, textAlign: 'center' }}>{t.createdAt ? t.createdAt.split('T')[0].replace(/-/g, '/') : '—'}</td>
                                                            <td style={{ padding: '8px 16px', fontSize: 12, color: MUTED, textAlign: 'center' }}>{t.createdAt ? t.createdAt.split('T')[1]?.substring(0, 5) : '—'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                </SectionCard>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default TradeProperty;
